import { h } from 'preact';
import { useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useBlackBarSizeDetector } from '../../common/creative-validators';
import { PreviewContext } from '../../common/creative/contexts/previewContext';
import { useAdParameters } from '../../common/creative/hooks/useAdParameters';
import { useCTAColor } from '../../common/creative/hooks/useCTAColor';
import { cssBlock, useDynamicCSS } from '../../common/creative/hooks/useDynamicCSS';
import { useMaxElementHeight } from '../../common/creative/hooks/useMaxElementHeight';
import { useMaxSlotSize } from '../../common/creative/hooks/useMaxSlotSize';
import { Platform } from '../../common/creative/hooks/usePlatform';
import { useResizeObserver } from '../../common/creative/hooks/useResizeObserver';
import { useRTL } from '../../common/creative/hooks/useRTL';
import { useTextKeywords } from '../../common/creative/hooks/useTextKeywords';
import { useUpdateSlotSizeWithMainImage } from '../../common/creative/hooks/useUpdateSlotSize';
import { Arrow } from '../../common/creative/icons/Arrow';
import { ImageCropWrapper } from '../../common/creative/image-crop-wrapper';
import { LineClamp } from '../../common/creative/line-clamp-2';
import { getLocaleForLanguage } from '../../common/creative/locales';
import { isRtlLanguage } from '../../common/creative/rtl-languages';
import { AssetComponentId } from '../../common/creative/social/asset-component';
import { useFontBoost } from '../../common/creative/with-font-boost';
import { classNames } from '../../common/css';
import { adapterDco } from './adapter-dco';
import { PerformanceSingleImageConfig, PerformanceSingleImageConfigDCO } from './config';
import classes from './creative.scss';

const mainImageRatio = 1.91;

export interface SelectedVariation {
  main_image: string;
  title: string;
  cta: string;
}

function useCSSForCTA(color: string | undefined) {
  const [normalCTAColor, hoverCTAColor] = useCTAColor(color);
  const setCTAColor = useDynamicCSS();

  useEffect(() => {
    setCTAColor(
      cssBlock(
        `.${classes.Creative} {`,
        `--cta-normal-color: ${normalCTAColor};`,
        `--cta-hover-color: ${hoverCTAColor};`,
        `}`
      )
    );
  }, [normalCTAColor, hoverCTAColor]);
}

const isRTL = (c: PerformanceSingleImageConfig) => isRtlLanguage(c.ctaLanguage);

export interface PerformanceSingleImageCreativeProps {
  onClick?: (evt: MouseEvent) => void;
  isCtaActive: boolean;

  platform: Platform;
  selectedVariation: SelectedVariation;
}

export function getSelectedVariationsIndexes(
  config: PerformanceSingleImageConfigDCO,
  selectedVariations: SelectedVariation
) {
  let main_image_temp = config.main_image.findIndex((variation) => variation.key == selectedVariations.main_image);
  if (main_image_temp == -1) main_image_temp = 0;

  let cta_temp = config.cta.findIndex((variation) => variation.key == selectedVariations.cta);
  if (cta_temp == -1) cta_temp = 0;

  let title_temp = config.title.findIndex((variation) => variation.key == selectedVariations.title);
  if (title_temp == -1) title_temp = 0;

  return {
    main_image: main_image_temp,
    title: title_temp,
    cta: cta_temp,
  };
}

export function PerformanceSingleImageCreative({
  onClick,
  isCtaActive,
  platform,
  selectedVariation,
}: PerformanceSingleImageCreativeProps) {
  useFontBoost();

  const creativeClasses = useMemo(
    () =>
      classNames(
        classes.Creative,
        platform === Platform.Desktop && classes.layoutDesktop,
        platform === Platform.Mobile && classes.layoutMobile,
        isCtaActive && classes.active
      ),
    [isCtaActive, platform]
  );

  const { config: oldConfig } = useAdParameters<PerformanceSingleImageConfig>();
  const config = useMemo(() => adapterDco(oldConfig), [oldConfig]);
  const selectedVariationsIndex = useMemo(() => {
    return getSelectedVariationsIndexes(config, selectedVariation);
  }, [config, selectedVariation]);

  const mainImage = config.main_image[selectedVariationsIndex.main_image].value;

  const locale = getLocaleForLanguage(config.ctaLanguage);

  const title = useTextKeywords(config.title[selectedVariationsIndex.title].value, locale);
  const description = useTextKeywords(config.description || '', locale);

  useCSSForCTA(config.ctaColor);

  const [mainImageEl, setMainImageEl] = useState<HTMLImageElement | null>(null);
  const [mainImageWrapperEl, setMainImageWrapperEl] = useState<HTMLDivElement | null>(null);
  useMaxElementHeight(mainImageWrapperEl, mainImageRatio);

  const isPreview = useContext(PreviewContext);

  const imgRealSize = useResizeObserver(mainImageWrapperEl);
  useBlackBarSizeDetector(
    imgRealSize.width,
    imgRealSize.height,
    mainImageEl?.naturalWidth ?? 0,
    mainImageEl?.naturalHeight ?? 0
  );

  const adRef = useRef<HTMLDivElement>(null);
  useUpdateSlotSizeWithMainImage(adRef.current, mainImageEl, imgRealSize);

  const rtlClassName = useRTL(isRTL);
  const maxSlotSize = useMaxSlotSize();

  const imageProps = useMemo(
    () => ({
      id: AssetComponentId,
      ref: setMainImageEl,
    }),
    []
  );

  return (
    <div style={maxSlotSize}>
      <div ref={adRef} onClick={onClick} className={classNames(creativeClasses, rtlClassName)}>
        <ImageCropWrapper
          containerRef={setMainImageWrapperEl}
          asset={mainImage}
          className={classNames(classes.Asset, isPreview && classes.preview)}
          imageProps={imageProps}
        />

        <div className={classes.Footer}>
          <div className={classes.TextBox}>
            <div className={classes.Brand}>
              {config.logo && config.logo.uri ? (
                <ImageCropWrapper asset={config.logo} className={classes.Logo} alt="" />
              ) : null}
              <span className={classes.BrandName}>{config.brand}</span>
              <span className={classes.Sponsored}>
                {'· '}
                {config.sponsored}
              </span>
            </div>
            <div className={classes.Title}>
              <LineClamp lines={2} text={title} dots keywords />
            </div>
            <div className={classes.Description}>
              <LineClamp lines={3} text={description} dots keywords />
            </div>
          </div>
          <div className={classes.CTABox}>
            <div className={classes.CTA}>
              <span className={classes.CTATextContainer}>{config.cta[selectedVariationsIndex.cta].value.ctaText}</span>
              <Arrow className={classes.CTAArrow} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
